import React from "react";
import { subIndustryOptionsByIndustry, industryOptions, goalOptions, toneOptions, ctaOptions, brandStyleOptions, pricePositioningOptions } from "../../data/websiteOptions";

const inputStyle = {};
const textareaStyle = {};
const selectStyle = {};

function Field({ label, children }) {
    return (
        <div className="wg-field">
            <label className="wg-label">{label}</label>
            {children}
        </div>
    );
}

function TwoCol({ children }) {
    return <div className="wg-grid-2">{children}</div>;
}

export default function BusinessDetailsStep({
    form,
    updateField,
    onAutofill,
    autofilling,
    mode,
    setMode,
    showOfferings,
    setShowOfferings,
    showAdvanced,
    setShowAdvanced
}) {
    const offeredItems = Array.isArray(form.offeredItems) ? form.offeredItems : [];

    const updateOfferedItem = (index, key, value) => {
        const next = [...offeredItems];
        next[index] = {
            ...next[index],
            [key]: value
        };
        updateField("offeredItems", next);
    };

    const addOfferedItem = () => {
        updateField("offeredItems", [
            ...offeredItems,
            {
                id: crypto.randomUUID(),
                name: "",
                subtitle: "",
                description: "",
                badge: "",
                category: "",
                audience: "",
                level: "",
                imagePreference: "",
                keyPoints: [""]
            }
        ]);
    };

    const removeOfferedItem = (index) => {
        const next = offeredItems.filter((_, i) => i !== index);
        updateField("offeredItems", next.length > 0 ? next : [{
            id: crypto.randomUUID(),
            name: "",
            subtitle: "",
            description: "",
            badge: "",
            category: "",
            audience: "",
            level: "",
            imagePreference: "",
            keyPoints: [""]
        }]);
    };

    const addKeyPoint = (itemIndex) => {
        const next = [...offeredItems];
        next[itemIndex] = {
            ...next[itemIndex],
            keyPoints: [...(next[itemIndex].keyPoints || []), ""]
        };
        updateField("offeredItems", next);
    };

    const updateKeyPoint = (itemIndex, pointIndex, value) => {
        const next = [...offeredItems];
        const points = [...(next[itemIndex].keyPoints || [])];
        points[pointIndex] = value;
        next[itemIndex] = {
            ...next[itemIndex],
            keyPoints: points
        };
        updateField("offeredItems", next);
    };

    const removeKeyPoint = (itemIndex, pointIndex) => {
        const next = [...offeredItems];
        const points = [...(next[itemIndex].keyPoints || [])].filter((_, i) => i !== pointIndex);
        next[itemIndex] = {
            ...next[itemIndex],
            keyPoints: points.length > 0 ? points : [""]
        };
        updateField("offeredItems", next);
    };

    return (
        <div className="wg-step-shell">
            <div className="wg-mode-bar">
                <div>
                    <div className="wg-section-title">Business Details</div>
                    <p className="wg-section-subtitle">
                        Start with the basics. AI will fill pages, content direction, offerings, and style.
                    </p>
                </div>

                <div className="wg-mode-toggle">
                    <button
                        type="button"
                        className={`wg-chip-btn ${mode === "fast" ? "active" : ""}`}
                        onClick={() => setMode("fast")}
                    >
                        Fast Mode
                    </button>
                    <button
                        type="button"
                        className={`wg-chip-btn ${mode === "detailed" ? "active" : ""}`}
                        onClick={() => setMode("detailed")}
                    >
                        Detailed Mode
                    </button>
                </div>
            </div>

            <div className="wg-ai-banner">
                <div>
                    <strong>AI Autofill</strong>
                    <p>
                        Give the basics first. AI will suggest audience, pages, tone, offerings, and structure.
                    </p>
                </div>

                <button
                    type="button"
                    className="wg-primary-btn"
                    onClick={onAutofill}
                    disabled={autofilling}
                >
                    {autofilling ? "Autofilling..." : "Use AI to Fill the Rest"}
                </button>
            </div>

            <div className="wg-block">
                <h3 className="wg-block-title">Essentials</h3>

                <TwoCol>
                    <Field label="Business Name">
                        <input
                            type="text"
                            value={form.businessName}
                            onChange={(e) => updateField("businessName", e.target.value)}
                            placeholder="e.g. Shriansh AI"
                            style={inputStyle}
                        />
                    </Field>

                    <Field label="Industry">
                        <select
                            value={form.industry}
                            onChange={(e) => {
                                const nextIndustry = e.target.value;
                                updateField("industry", nextIndustry);

                                const nextSuggestions = subIndustryOptionsByIndustry[nextIndustry] || [];
                                if (nextSuggestions.length > 0 && !nextSuggestions.includes(form.subIndustry)) {
                                    updateField("subIndustry", "");
                                }
                            }}
                            style={selectStyle}
                        >
                            <option value="">Select Industry</option>
                            {industryOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </Field>
                </TwoCol>

                <TwoCol>
                    <Field label="Sub Industry">
                        {subIndustryOptionsByIndustry[form.industry]?.length > 0 ? (
                            <>
                                <select
                                    value={form.subIndustry}
                                    onChange={(e) => updateField("subIndustry", e.target.value)}
                                    style={selectStyle}
                                >
                                    <option value="">Select Sub Industry</option>
                                    {subIndustryOptionsByIndustry[form.industry].map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>

                                <div style={{ height: 10 }} />

                                <input
                                    type="text"
                                    value={form.subIndustry}
                                    onChange={(e) => updateField("subIndustry", e.target.value)}
                                    placeholder="Or type a custom sub industry"
                                    style={inputStyle}
                                />
                            </>
                        ) : (
                            <input
                                type="text"
                                value={form.subIndustry}
                                onChange={(e) => updateField("subIndustry", e.target.value)}
                                placeholder="e.g. AI Automation, CRM, Web Apps"
                                style={inputStyle}
                            />
                        )}
                    </Field>

                    <Field label="Goal of Website">
                        <select
                            value={form.goalOfWebsite}
                            onChange={(e) => updateField("goalOfWebsite", e.target.value)}
                            style={selectStyle}
                        >
                            <option value="">Select Goal</option>
                            {goalOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </Field>
                </TwoCol>

                <TwoCol>
                    <Field label="Location">
                        <input
                            type="text"
                            value={form.location}
                            onChange={(e) => updateField("location", e.target.value)}
                            placeholder="e.g. Ahmedabad, India"
                            style={inputStyle}
                        />
                    </Field>

                    <Field label="Primary Audience">
                        <input
                            type="text"
                            value={form.primaryAudience}
                            onChange={(e) => updateField("primaryAudience", e.target.value)}
                            placeholder="e.g. Startups, business owners, shoppers"
                            style={inputStyle}
                        />
                    </Field>
                </TwoCol>

                <Field label="Description">
                    <textarea
                        value={form.description}
                        onChange={(e) => updateField("description", e.target.value)}
                        placeholder="Describe what the business does, who it helps, and what makes it valuable."
                        rows={5}
                        style={textareaStyle}
                    />
                </Field>
            </div>

            <div className="wg-block">
                <div className="wg-block-header">
                    <div>
                        <h3 className="wg-block-title">Recommended Pages</h3>
                        <p className="wg-block-subtitle">AI can adjust these later based on your business type.</p>
                    </div>
                </div>

                <div className="wg-chip-row">
                    {(form.pages || []).map((page) => (
                        <span key={page} className="wg-chip">
                            {page}
                        </span>
                    ))}
                </div>
            </div>

            <div className="wg-block">
                <div className="wg-block-header">
                    <div>
                        <h3 className="wg-block-title">Offerings / Products / Programs</h3>
                        <p className="wg-block-subtitle">
                            Optional. Keep collapsed for fast generation, or expand to guide the showcase more precisely.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="wg-secondary-btn"
                        onClick={() => setShowOfferings((prev) => !prev)}
                    >
                        {showOfferings ? "Hide Offerings" : "Show Offerings"}
                    </button>
                </div>

                {showOfferings && (
                    <>
                        <div className="wg-offering-topbar">
                            <button
                                type="button"
                                className="wg-secondary-btn"
                                onClick={addOfferedItem}
                            >
                                + Add Item
                            </button>
                        </div>

                        {offeredItems.map((item, itemIndex) => (
                            <div className="wg-offering-card" key={item.id || itemIndex}>
                                <div className="wg-offering-head">
                                    <h4>Item {itemIndex + 1}</h4>
                                    <button
                                        type="button"
                                        className="wg-danger-btn"
                                        onClick={() => removeOfferedItem(itemIndex)}
                                    >
                                        Remove
                                    </button>
                                </div>

                                <TwoCol>
                                    <Field label="Name">
                                        <input
                                            type="text"
                                            value={item.name || ""}
                                            onChange={(e) => updateOfferedItem(itemIndex, "name", e.target.value)}
                                            style={inputStyle}
                                        />
                                    </Field>

                                    <Field label="Subtitle">
                                        <input
                                            type="text"
                                            value={item.subtitle || ""}
                                            onChange={(e) => updateOfferedItem(itemIndex, "subtitle", e.target.value)}
                                            style={inputStyle}
                                        />
                                    </Field>
                                </TwoCol>

                                <TwoCol>
                                    <Field label="Badge">
                                        <input
                                            type="text"
                                            value={item.badge || ""}
                                            onChange={(e) => updateOfferedItem(itemIndex, "badge", e.target.value)}
                                            style={inputStyle}
                                        />
                                    </Field>

                                    <Field label="Category">
                                        <input
                                            type="text"
                                            value={item.category || ""}
                                            onChange={(e) => updateOfferedItem(itemIndex, "category", e.target.value)}
                                            style={inputStyle}
                                        />
                                    </Field>
                                </TwoCol>

                                <TwoCol>
                                    <Field label="Audience">
                                        <input
                                            type="text"
                                            value={item.audience || ""}
                                            onChange={(e) => updateOfferedItem(itemIndex, "audience", e.target.value)}
                                            style={inputStyle}
                                        />
                                    </Field>

                                    <Field label="Level / Type">
                                        <input
                                            type="text"
                                            value={item.level || ""}
                                            onChange={(e) => updateOfferedItem(itemIndex, "level", e.target.value)}
                                            style={inputStyle}
                                        />
                                    </Field>
                                </TwoCol>

                                <Field label="Image Preference">
                                    <input
                                        type="text"
                                        value={item.imagePreference || ""}
                                        onChange={(e) => updateOfferedItem(itemIndex, "imagePreference", e.target.value)}
                                        placeholder="e.g. premium product shot, studio image, lifestyle image"
                                        style={inputStyle}
                                    />
                                </Field>

                                <Field label="Description">
                                    <textarea
                                        value={item.description || ""}
                                        onChange={(e) => updateOfferedItem(itemIndex, "description", e.target.value)}
                                        rows={4}
                                        style={textareaStyle}
                                    />
                                </Field>

                                <div className="wg-keypoints-head">
                                    <h5>Key Points</h5>
                                    <button
                                        type="button"
                                        className="wg-secondary-btn"
                                        onClick={() => addKeyPoint(itemIndex)}
                                    >
                                        + Add Point
                                    </button>
                                </div>

                                {(item.keyPoints || []).map((point, pointIndex) => (
                                    <div className="wg-keypoint-row" key={`${itemIndex}-${pointIndex}`}>
                                        <input
                                            type="text"
                                            value={point || ""}
                                            onChange={(e) => updateKeyPoint(itemIndex, pointIndex, e.target.value)}
                                            placeholder={`Point ${pointIndex + 1}`}
                                            style={inputStyle}
                                        />
                                        <button
                                            type="button"
                                            className="wg-danger-btn"
                                            onClick={() => removeKeyPoint(itemIndex, pointIndex)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </>
                )}
            </div>

            {(mode === "detailed" || showAdvanced) && (
                <div className="wg-block">
                    <div className="wg-block-header">
                        <div>
                            <h3 className="wg-block-title">Advanced Options</h3>
                            <p className="wg-block-subtitle">
                                Optional controls for users who want tighter creative direction.
                            </p>
                        </div>

                        <button
                            type="button"
                            className="wg-secondary-btn"
                            onClick={() => setShowAdvanced((prev) => !prev)}
                        >
                            {showAdvanced ? "Hide Advanced" : "Show Advanced"}
                        </button>
                    </div>

                    {showAdvanced && (
                        <>
                            <TwoCol>
                                <Field label="Tone">
                                    <select
                                        value={form.tone}
                                        onChange={(e) => updateField("tone", e.target.value)}
                                        style={selectStyle}
                                    >
                                        {toneOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </Field>

                                <Field label="Secondary Audience">
                                    <input
                                        type="text"
                                        value={form.secondaryAudience}
                                        onChange={(e) => updateField("secondaryAudience", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>
                            </TwoCol>

                            <TwoCol>
                                <Field label="Service Areas">
                                    <input
                                        type="text"
                                        value={form.serviceAreas}
                                        onChange={(e) => updateField("serviceAreas", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>

                                <Field label="CTA Type">
                                    <select
                                        value={form.callToActionType}
                                        onChange={(e) => updateField("callToActionType", e.target.value)}
                                        style={selectStyle}
                                    >
                                        <option value="">Select CTA</option>
                                        {ctaOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                            </TwoCol>

                            <TwoCol>
                                <Field label="Brand Style">
                                    <select
                                        value={form.brandStyle}
                                        onChange={(e) => updateField("brandStyle", e.target.value)}
                                        style={selectStyle}
                                    >
                                        <option value="">Select Brand Style</option>
                                        {brandStyleOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </Field>

                                <Field label="Price Positioning">
                                    <select
                                        value={form.pricePositioning}
                                        onChange={(e) => updateField("pricePositioning", e.target.value)}
                                        style={selectStyle}
                                    >
                                        <option value="">Select Positioning</option>
                                        {pricePositioningOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                            </TwoCol>

                            <Field label="Core Services">
                                <textarea
                                    value={form.coreServices}
                                    onChange={(e) => updateField("coreServices", e.target.value)}
                                    rows={4}
                                    style={textareaStyle}
                                />
                            </Field>

                            <Field label="Project Types / Products">
                                <textarea
                                    value={form.projectTypes}
                                    onChange={(e) => updateField("projectTypes", e.target.value)}
                                    rows={4}
                                    style={textareaStyle}
                                />
                            </Field>

                            <Field label="Unique Selling Points">
                                <textarea
                                    value={form.uniqueSellingPoints}
                                    onChange={(e) => updateField("uniqueSellingPoints", e.target.value)}
                                    rows={4}
                                    style={textareaStyle}
                                />
                            </Field>

                            <Field label="Trust Signals">
                                <textarea
                                    value={form.trustSignals}
                                    onChange={(e) => updateField("trustSignals", e.target.value)}
                                    rows={4}
                                    style={textareaStyle}
                                />
                            </Field>

                            <TwoCol>
                                <Field label="Years in Business">
                                    <input
                                        type="text"
                                        value={form.yearsInBusiness}
                                        onChange={(e) => updateField("yearsInBusiness", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>

                                <Field label="Visual Tone">
                                    <input
                                        type="text"
                                        value={form.visualTone}
                                        onChange={(e) => updateField("visualTone", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>
                            </TwoCol>

                            <TwoCol>
                                <Field label="Must Include Words">
                                    <input
                                        type="text"
                                        value={form.mustIncludeWords}
                                        onChange={(e) => updateField("mustIncludeWords", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>

                                <Field label="Avoid Words">
                                    <input
                                        type="text"
                                        value={form.avoidWords}
                                        onChange={(e) => updateField("avoidWords", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>
                            </TwoCol>

                            <TwoCol>
                                <Field label="Competitor Website URL">
                                    <input
                                        type="text"
                                        value={form.competitorWebsiteUrl}
                                        onChange={(e) => updateField("competitorWebsiteUrl", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>

                                <Field label="Reference Website Type">
                                    <input
                                        type="text"
                                        value={form.referenceWebsiteType}
                                        onChange={(e) => updateField("referenceWebsiteType", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>
                            </TwoCol>

                            <Field label="Competitor Style Notes">
                                <textarea
                                    value={form.competitorStyleNotes}
                                    onChange={(e) => updateField("competitorStyleNotes", e.target.value)}
                                    rows={3}
                                    style={textareaStyle}
                                />
                            </Field>

                            <TwoCol>
                                <Field label="Business Model">
                                    <input
                                        type="text"
                                        value={form.businessModel}
                                        onChange={(e) => updateField("businessModel", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>

                                <Field label="Primary Website Mode">
                                    <input
                                        type="text"
                                        value={form.primaryWebsiteMode}
                                        onChange={(e) => updateField("primaryWebsiteMode", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>
                            </TwoCol>

                            <TwoCol>
                                <Field label="Primary Entity Type">
                                    <input
                                        type="text"
                                        value={form.primaryEntityType}
                                        onChange={(e) => updateField("primaryEntityType", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>

                                <Field label="Location Focus">
                                    <input
                                        type="text"
                                        value={form.locationFocus}
                                        onChange={(e) => updateField("locationFocus", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>
                            </TwoCol>

                            <TwoCol>
                                <Field label="Desired Layout Style">
                                    <input
                                        type="text"
                                        value={form.desiredLayoutStyle}
                                        onChange={(e) => updateField("desiredLayoutStyle", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>

                                <Field label="Content Style Hint">
                                    <input
                                        type="text"
                                        value={form.contentStyleHint}
                                        onChange={(e) => updateField("contentStyleHint", e.target.value)}
                                        style={inputStyle}
                                    />
                                </Field>
                            </TwoCol>

                            <Field label="Preferred Section Density">
                                <input
                                    type="text"
                                    value={form.preferredSectionDensity}
                                    onChange={(e) => updateField("preferredSectionDensity", e.target.value)}
                                    style={inputStyle}
                                />
                            </Field>
                        </>
                    )}

                    {mode === "fast" && !showAdvanced && (
                        <div className="wg-advanced-compact">
                            <button
                                type="button"
                                className="wg-secondary-btn"
                                onClick={() => setShowAdvanced(true)}
                            >
                                Show Advanced
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}